"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
const CartContext = React.createContext(undefined);
const fingerprint = (id, customKey = 'default') => `${id}::${customKey}`;
const normalizeQuantity = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0)
        return 1;
    return Math.floor(parsed);
};
const mergeEntries = (entries) => {
    var _a, _b, _c, _d;
    const map = new Map();
    for (const entry of entries) {
        const existing = map.get(entry.key);
        if (existing) {
            map.set(entry.key, Object.assign(Object.assign({}, existing), { quantity: existing.quantity + entry.quantity, name: existing.name || entry.name, price: existing.price || entry.price, image: (_b = (_a = existing.image) !== null && _a !== void 0 ? _a : entry.image) !== null && _b !== void 0 ? _b : null, url: (_d = (_c = existing.url) !== null && _c !== void 0 ? _c : entry.url) !== null && _d !== void 0 ? _d : null }));
        }
        else {
            map.set(entry.key, Object.assign({}, entry));
        }
    }
    return Array.from(map.values());
};
const mapAgentEntries = (state) => {
    var _a;
    return ((_a = state.items) !== null && _a !== void 0 ? _a : [])
        .map((item) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        const resolvedId = String((_c = (_b = (_a = item.product) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : item.product_id) !== null && _c !== void 0 ? _c : '').trim();
        if (!resolvedId)
            return null;
        const rawName = ((_f = (_e = (_d = item.product) === null || _d === void 0 ? void 0 : _d.name) !== null && _e !== void 0 ? _e : item.product_id) !== null && _f !== void 0 ? _f : 'Item').trim();
        // Trim noisy confirmation phrases from product names, e.g.
        // "The MacBook Air has been successfully added to your cart..."
        const cleanedName = (() => {
            if (!rawName)
                return 'Item';
            const match = rawName.match(/^(.+?)\s+has been (successfully )?added to your (cart|basket)\b.*$/i);
            if (match && match[1]) {
                return match[1].trim();
            }
            return rawName;
        })();
        const fingerprintKey = fingerprint(resolvedId, item.custom_fields && item.custom_fields.length ? JSON.stringify(item.custom_fields) : 'agent');
        return {
            key: fingerprintKey,
            id: resolvedId,
            name: cleanedName || 'Item',
            price: (_h = (_g = item.product) === null || _g === void 0 ? void 0 : _g.price) !== null && _h !== void 0 ? _h : '',
            image: (_k = (_j = item.product) === null || _j === void 0 ? void 0 : _j.image) !== null && _k !== void 0 ? _k : null,
            url: (_m = (_l = item.product) === null || _l === void 0 ? void 0 : _l.url) !== null && _m !== void 0 ? _m : null,
            quantity: normalizeQuantity(item.quantity),
        };
    })
        .filter(Boolean);
};
export function CartProvider({ children }) {
    const [entries, setEntries] = React.useState([]);
    const [isCartOpen, setIsCartOpen] = React.useState(false);
    const openCart = React.useCallback(() => setIsCartOpen(true), []);
    const closeCart = React.useCallback(() => setIsCartOpen(false), []);
    const toggleCart = React.useCallback(() => setIsCartOpen((prev) => !prev), []);
    const ingestAgentCart = React.useCallback((state) => {
        if (!state || !Array.isArray(state.items))
            return;
        const agentEntries = mapAgentEntries(state);
        setEntries(agentEntries);
    }, []);
    const cartCount = React.useMemo(() => entries.reduce((sum, entry) => sum + (Number(entry.quantity) || 0), 0), [entries]);
    React.useEffect(() => {
        if (entries.length === 0) {
            setIsCartOpen(false);
        }
    }, [entries.length]);
    const value = React.useMemo(() => ({
        items: entries,
        cartCount,
        isCartOpen,
        openCart,
        closeCart,
        toggleCart,
        ingestAgentCart,
    }), [entries, cartCount, isCartOpen, openCart, closeCart, toggleCart, ingestAgentCart]);
    return _jsx(CartContext.Provider, { value: value, children: children });
}
export function useCart() {
    const ctx = React.useContext(CartContext);
    if (!ctx) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return ctx;
}
